import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, Plus, X, ChevronDown, ChevronRight, Clock, CheckCircle, AlertTriangle, Truck } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { Modal } from '../../components/ui/Modal';
import { usePedidos, usePedidoDetail, useCancelPedido } from '../../hooks/usePedidos';
import { useAuthStore } from '../../stores/auth-store';
import { toast } from '../../components/ui/Toast';

const ESTADO_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3" /> },
  despachado_parcial: { label: 'Despachado', color: 'bg-blue-100 text-blue-800', icon: <Truck className="w-3 h-3" /> },
  despachado_total: { label: 'Despachado', color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
  cancelado: { label: 'Cancelado', color: 'bg-gray-100 text-gray-500', icon: <X className="w-3 h-3" /> },
};

const TIPO_LABELS: Record<string, string> = {
  materiales: 'Materiales',
  equipos: 'Equipos',
  ambos: 'Materiales y Equipos',
};

export function PedidosPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [estadoFilter, setEstadoFilter] = useState<string | undefined>();
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelEstado, setCancelEstado] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, error } = usePedidos(projectId!, { estado: estadoFilter });
  const cancelPedido = useCancelPedido();

  const canCreate = user?.role === 'residente' || ['gerente', 'operaciones', 'administrativo'].includes(user?.role ?? '');

  const handleCancel = async () => {
    if (!cancelId) return;
    try {
      await cancelPedido.mutateAsync({ pedidoId: cancelId });
      toast('success', 'Pedido cancelado');
      setCancelId(null);
      setCancelEstado(null);
    } catch {
      toast('error', 'Error al cancelar pedido');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600 mb-2">Error al cargar pedidos</p>
        <Button variant="secondary" onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  const pedidos = data?.data ?? [];

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Package className="w-5 h-5" />
          Pedidos de Materiales
        </h1>
        {canCreate && (
          <Button onClick={() => navigate(`/proyectos/${projectId}/pedidos/nuevo`)}>
            <Plus className="w-4 h-4 mr-1" />
            Nuevo Pedido
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <button
          className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${!estadoFilter ? 'bg-[#3c98ff] text-white' : 'bg-white/60 text-gray-600'}`}
          onClick={() => setEstadoFilter(undefined)}
        >
          Todos
        </button>
        {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${estadoFilter === key ? 'bg-[#3c98ff] text-white' : 'bg-white/60 text-gray-600'}`}
            onClick={() => setEstadoFilter(key)}
          >
            {cfg.label}
          </button>
        ))}
      </div>

      {/* List */}
      {pedidos.length === 0 ? (
        <Card className="p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay pedidos{estadoFilter ? ` con estado "${ESTADO_CONFIG[estadoFilter]?.label}"` : ''}</p>
          {canCreate && (
            <Button className="mt-4" onClick={() => navigate(`/proyectos/${projectId}/pedidos/nuevo`)}>
              Crear primer pedido
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {pedidos.map((p) => {
            const cfg = ESTADO_CONFIG[p.estado] ?? ESTADO_CONFIG.pendiente;
            const canCancelThis =
              (p.estado === 'pendiente' && (user?.role !== 'residente' || p.created_by.id === user?.id)) ||
              (['despachado_parcial', 'despachado_total'].includes(p.estado) && user?.role !== 'residente');

            const isExpanded = expandedId === p.id;
            return (
              <Card key={p.id} className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : p.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {p.numero_orden != null && (
                        <span className="text-xs font-mono text-gray-500 font-semibold">
                          #{p.numero_orden}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                        {cfg.icon}
                        {cfg.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {TIPO_LABELS[p.tipo] ?? p.tipo}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {p.items_count} ítem{p.items_count !== 1 ? 's' : ''} &middot; {p.created_by.nombre}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(p.created_at).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                    {p.notas && (
                      <p className="text-xs text-gray-400 mt-1 truncate">{p.notas}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {canCancelThis && (
                      <button
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Cancelar pedido"
                        onClick={(e) => { e.stopPropagation(); setCancelId(p.id); setCancelEstado(p.estado); }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-blue-500" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>
                {isExpanded && <PedidoDetailPanel pedidoId={p.id} />}
              </Card>
            );
          })}
        </div>
      )}

      {/* Cancel confirmation */}
      <Modal isOpen={!!cancelId} onClose={() => { setCancelId(null); setCancelEstado(null); }} title="Cancelar Pedido" size="sm">
        <div className="space-y-4">
          <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800">
              {['despachado_parcial', 'despachado_total'].includes(cancelEstado ?? '')
                ? 'Este pedido ya fue despachado. Al cancelarlo, los equipos despachados serán devueltos al inventario y los costos de insumos adicionales serán revertidos.'
                : 'Esta acción no se puede deshacer. El pedido quedará cancelado permanentemente.'}
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => { setCancelId(null); setCancelEstado(null); }}>Volver</Button>
            <Button variant="danger" onClick={handleCancel} isLoading={cancelPedido.isPending}>
              Cancelar Pedido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Pedido Detail Panel (expandable) ────────────────────────────────

function PedidoDetailPanel({ pedidoId }: { pedidoId: string }) {
  const { data: detail, isLoading } = usePedidoDetail(pedidoId);

  if (isLoading) return <div className="mt-3 text-xs text-gray-400">Cargando detalle...</div>;
  if (!detail) return <div className="mt-3 text-xs text-gray-400">No se pudo cargar el detalle</div>;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs font-medium text-gray-500 mb-2">Items del pedido:</p>
      <div className="space-y-1">
        {detail.items.map((item) => (
          <div key={item.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-800 truncate">
                {item.material_nombre ?? item.equipo_nombre ?? item.nombre_custom ?? 'Item'}
              </p>
              {item.material_sku && <p className="text-[10px] text-gray-400">{item.material_sku}</p>}
              {item.es_consumible_adicional && <span className="text-[10px] text-amber-600 font-medium">Consumible adicional</span>}
              {item.excede_ot && <span className="text-[10px] text-red-600 font-medium ml-2">Excede OT</span>}
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-800">{item.cantidad} {item.material_unidad ?? item.unidad_custom ?? ''}</p>
              {item.justificacion && <p className="text-[10px] text-gray-400 max-w-[150px] truncate">{item.justificacion}</p>}
            </div>
          </div>
        ))}
      </div>
      {detail.notas && (
        <p className="text-xs text-gray-500 mt-2 italic">Notas: {detail.notas}</p>
      )}
    </div>
  );
}
